import { i18n } from '@lingui/core';
import { msg, plural, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { resolvePatternText } from '@/shared/patterns/text';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { authorizationFilters } from './authorization.filters';

// Manifesto da Gestão de autorizações (U23/U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K.
// Só leitura: nenhuma ação altera dado (`AuthorizationListHeader.tsx:24-33`, `AuthorizationList.tsx:138-145`).

export const AUTHORIZATIONS_PATH = '/ccb/gestao-autorizacoes';

/** "Atualizar" do cabeçalho. A tela liga o `run` ao refetch da lista. */
export const authorizationsRefresh = action({
  id: 'dataprevEmployee.authorizations.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de autorizações de consulta de margem na Dataprev, sem mudar nada.`,
  run: () => undefined,
});

/** Ícone de copiar da linha, só quando há `authorizationLink` (`AuthorizationList.tsx:138-145`). */
export const authorizationsCopyLink = action({
  id: 'dataprevEmployee.authorizations.copyLink',
  kind: 'read',
  row: true,
  label: msg`Copiar link de autorização`,
  description: msg`Copia para a área de transferência o link de autorização enviado ao trabalhador, de uma linha da lista (linha sem link não tem a ação). Não muda nada.`,
  input: z.object({ link: z.string().min(1).max(2000) }),
  run: async ({ input }) => {
    await navigator.clipboard.writeText(input.link);
  },
});

export const authorizationsFilter = filterAction('dataprevEmployee.authorizations.filter', authorizationFilters, {
  description: msg`Filtra a lista de autorizações: registrationNumber (CPF do trabalhador, como digitado, com ou sem máscara), phoneNumber (WhatsApp), status (Approved aprovada, Refused recusada, Pending pendente) e o período da solicitação initialDate/finalDate (AAAA-MM-DD). Filtro omitido é limpo.`,
});

export const authorizationsPage = listPageAction('dataprevEmployee.authorizations.page');

export const authorizationsScreen = defineScreen({
  id: 'dataprevEmployee.authorizations',
  path: AUTHORIZATIONS_PATH,
  title: msg`Gestão de autorizações`,
  description: msg`Autorizações pedidas aos trabalhadores para a consulta de margem consignável na Dataprev (Crédito do Trabalhador): lista, filtro por CPF, WhatsApp, status e data da solicitação, e cópia do link de autorização. Só leitura.`,
  // Herdada do grupo Crédito (`router.tsx:995-1001`); a rota não tem permissão própria.
  permission: can('CreditNote', 'Read'),
  access: { flag: { key: 'ENABLE_AUTHORIZATION_MANAGEMENT', notEquals: 0 } },
  place: { icon: 'badge-check', section: 'credit-note' },
  actions: [authorizationsRefresh, authorizationsFilter, authorizationsPage, authorizationsCopyLink],
});

/** O que o agente lê da tela (U24, U46): contagem e filtros pelo rótulo, sem CPF nem telefone, no idioma da tela. */
export function authorizationsContext(input: { readonly total: number | undefined; readonly filters: readonly string[] }): ScreenContext {
  const total = input.total;
  const count = total === undefined ? t`Carregando.` : plural(total, { one: '# autorização na lista.', other: '# autorizações na lista.' });
  const labels = input.filters.map((key) => {
    const label = authorizationFilters.fieldOf(key)?.label;
    return label ? resolvePatternText(i18n, label) : key;
  });
  const filterList = labels.join(', ');
  const filters = labels.length > 0 ? t`Filtros: ${filterList}.` : t`Sem filtros.`;
  return {
    summary: [t`Gestão de autorizações de consulta de margem na Dataprev.`, count, filters].join(' '),
    fields: { total: input.total ?? null },
    sensitivity: 'restricted',
  };
}
